import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { createReducer, on } from "@ngrx/store";
import { DhmoInsurancePolicyDto } from "src/app/core/models/http/treatment/insurance-policies/dhmo-insurance-policy-dto";
import { PpoInsurancePolicyDto } from "src/app/core/models/http/treatment/insurance-policies/ppo-insurance-policy-dto";
import {
  InsuranceApiActions,
  TreatmentPageActions,
  TreatmentPlanApiActions,
} from "../actions/action-index";
import { InsurancePolicy } from "./insurance-policy";

export const featureKey = "insurancePolicies";

export interface InsurancePolicyState extends EntityState<InsurancePolicy> {
  /** Track if the creation of a policy(s) is in progress. */
  isSavingPolicies: boolean;
  tabIndex: number;
}

export const adapter = createEntityAdapter<InsurancePolicy>({
  selectId: (insurance) => insurance.insurancePolicyPatientEligibilityId,
});

export const initialState: InsurancePolicyState = adapter.getInitialState({
  tabIndex: 0,
  isSavingPolicies: false
});

export const reducer = createReducer(
  initialState,
  on(TreatmentPlanApiActions.loadSuccess, (state, { contract }) => ({
    ...adapter.setAll(
      contract.InsuranceDetails.map(
        (id) =>
          ({
            insurancePolicyPatientEligibilityId:
              id.InsurancePolicyPatientEligibilityId,
            assignmentOfBenefit: id.AssignmentOfBenefit,
            copayPercentage: (id as PpoInsurancePolicyDto).CopayPercentage,
            inNetwork: id.InNetwork,
            insuranceCarrierId: id.InsuranceCarrierId,
            insuranceCarrierName: id.InsuranceCarrierName,
            insurancePlanPriority: id.InsurancePlanPriority,
            insuranceVerificationStatus: {
              id: id.InsuranceVerificationStatus.Id,
              name: id.InsuranceVerificationStatus.Name,
            },
            leasedNetworkName: id.LeasedNetworkName,
            lifetimeRemaining: (id as PpoInsurancePolicyDto).LifetimeRemaining,
            maxAge: id.MaxAge,
            minAge: id.MinAge,
            planType: id.PlanType,
            networkType: id.InsuranceNetworkType,
            copayFeeScheduleId: (id as DhmoInsurancePolicyDto)
              .CopayFeeScheduleId,
          } as InsurancePolicy)
      ),
      state
    ),
  })),
  on(
    TreatmentPageActions.assignmentOfBenefitChanged,
    (state, { insurancePolicyPatientEligibilityId, assignmentOfBenefit }) => {
      return adapter.updateOne(
        {
          id: insurancePolicyPatientEligibilityId,
          changes: {
            assignmentOfBenefit,
          },
        },
        state
      );
    }
  ),
  on(TreatmentPageActions.insurancePolicySelected, (state, { tabIndex }) => ({
    ...state,
    tabIndex,
  } ) ),
  on( TreatmentPageActions.feeDetailModalEntered, ( state ) => ( {
    ...state,
    tabIndex: 0,
  })),
  on(
    InsuranceApiActions.createInsurancePolicySuccess,
    ( state, action ) => adapter.addOne(
      action.insurancePolicy,
      { ...state, isSavingPolicies: false }
    )
  ),
  on( 
    InsuranceApiActions.createInsurancePolicyFail,
    ( state, action ) => ( { ...state, isSavingPolicies: false } )
  ),
  on(
    InsuranceApiActions.removeInsurancePolicySuccess,
    ( state, action ) => adapter.removeOne(
      action.id,
      { ...state, isSavingPolicies: false }
    )
  ),
  on(
    TreatmentPageActions.createInsurancePolicy,
    ( state, action ) => ( { ...state, isSavingPolicies: true } )
  ),
);
